import * as React from "react"
import { connect } from "react-redux"
import StickyBox from "react-sticky-box"

import { FieldItems, Input } from "@tenforce/toolbox-field-react"

import { GlobalState } from "../../../../../store"
import { formatString } from "../../../../../util/Strings"

import { DispatchProps, mapDispatchToProps } from "../../../actions"
import { getFilteredTerms } from "../../../selectors"

import Separator from "../../../../common/Separator"

import LoadMore from "./LoadMore"
import LoadingPlaceHolder from "./LoadingPlaceholder"

import { Labels } from "../../../../../util/Translations"
import { ContextualHelpTerm } from "../../../../../models"
import { IPagination } from "../../../index"

interface StateProps {
    filter: string
    filteredTerms: ContextualHelpTerm[]
    isLoading: boolean
    termPagination: IPagination
    initialSelectedTermCode: string | null
}

type Props = StateProps & DispatchProps

interface State {
    currentTerm: ContextualHelpTerm | null
}

export class DictionaryPopup extends React.Component<Props, State> {
    termsListContainer: React.RefObject<HTMLDivElement>

    constructor(props: Props) {
        super(props)

        const {
            initialSelectedTermCode,
            filteredTerms,
            selectTermInDictionary,
        } = props
        let selectedTerm = null

        if (initialSelectedTermCode) {
            selectedTerm = filteredTerms.find(
                (t: ContextualHelpTerm) => t.code === initialSelectedTermCode,
            ) as ContextualHelpTerm
        } else if (filteredTerms.length > 0) {
            selectedTerm = filteredTerms[0]
            selectTermInDictionary(selectedTerm.code)
        }

        this.state = {
            currentTerm: selectedTerm,
        }

        this.termsListContainer = React.createRef()
    }

    render(): JSX.Element {
        const {
            filter,
            filteredTerms,
            isLoading,
            termPagination: { page, size },
        } = this.props
        const { currentTerm } = this.state
        const { Dictionary, SearchByX, Title } = Labels

        const shownTerms = filteredTerms.slice(0, page * size)

        return (
            <div className="bg-lightest" id="dictionary">
                <div className="section-header">
                    <h2>{Dictionary}</h2>
                </div>

                <Separator />

                <div className="tw-flex tw-px-1 tw-py-2">
                    <div
                        className="tw-w-1/3 tw-m-2"
                        ref={this.termsListContainer}
                    >
                        <FieldItems>
                            <Input
                                placeholder={formatString(
                                    SearchByX,
                                    Title.toLowerCase(),
                                )}
                                type="search"
                                autoFocus={true}
                                value={filter}
                                id="dictionaryFilter"
                                onChangedValue={this.updateFilter}
                            />
                        </FieldItems>
                        {isLoading ? (
                            <LoadingPlaceHolder />
                        ) : (
                            <div>
                                {shownTerms.map(
                                    (term: ContextualHelpTerm): JSX.Element => (
                                        <button
                                            key={term.code}
                                            className={`theme-thumb theme-thumb--wide bg-cloud ${
                                                currentTerm?.code === term.code
                                                    ? "is-focused"
                                                    : ""
                                            }`}
                                            onClick={(): void => {
                                                this.selectTerm(term)
                                            }}
                                            data-ft-component="term"
                                            data-ft-term-code={term.code}
                                            data-ft-term-label={term.label}
                                            data-ft-term-target={term.target}
                                        >
                                            {term.label}
                                        </button>
                                    ),
                                )}

                                <LoadMore
                                    totalNumberOfTerms={filteredTerms.length}
                                />
                            </div>
                        )}
                    </div>

                    <StickyBox
                        className="tw-w-2/3 tw-mr-4 tw-my-2 tw-pb-4 bg-white theme-float tw-self-start"
                        offsetTop={16}
                    >
                        {!!currentTerm && (
                            <div data-ft-component="selected-term">
                                <div className="step-header">
                                    <h3 className="tw-my-2" data-ft-label>
                                        {currentTerm.label}
                                    </h3>
                                </div>
                                <div
                                    className="tw-py-2 tw-px-8 tw-whitespace-pre-line"
                                    data-ft-content
                                    dangerouslySetInnerHTML={{
                                        __html: currentTerm.content || "",
                                    }}
                                />
                            </div>
                        )}
                    </StickyBox>
                </div>
            </div>
        )
    }

    updateFilter = (filter: string): void => {
        this.props.filterTerms(filter)
    }

    selectTerm = (term: ContextualHelpTerm): void => {
        this.setState(
            {
                currentTerm: term,
            },
            () => {
                this.props.selectTermInDictionary(term.code)
            },
        )
    }
}

const mapStateToProps = (state: GlobalState): StateProps => ({
    filter: state.contextualHelp.dictionaryPopup.termsFilter,
    filteredTerms: getFilteredTerms(state.contextualHelp),
    isLoading: state.contextualHelp.dictionaryPopup.termsIsLoading,
    termPagination: state.contextualHelp.dictionaryPopup.termsPagination,
    initialSelectedTermCode:
        state.contextualHelp.dictionaryPopup.selectedTermCode,
})

export default connect(mapStateToProps, mapDispatchToProps)(DictionaryPopup)
